import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Pencil, Upload, Download, FileSpreadsheet, Trash2 } from "lucide-react";

const EMPTY = { name: "", region_id: "", area: "", region: "", state: "", district: "", contact_person: "", phone: "", gst: "", payment_terms: "", status: "Active", notes: "", territory_id: "", territory_name: "" };

function BulkUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null); const [busy, setBusy] = useState(false); const [report, setReport] = useState(null);
  useEffect(() => { if (!open) { setFile(null); setReport(null); } }, [open]);
  async function downloadTemplate() {
    const token = localStorage.getItem("oms_token");
    const res = await fetch(`${API}/distributors/template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
    const b = await res.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "distributors_template.xlsx"; a.click();
  }
  async function upload() {
    if (!file) return toast.error("Select file"); setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/distributors/bulk-upload`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed");
      setReport(d); toast.success(`Created ${d.created}, skipped ${d.skipped_duplicates}`); onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle className="heading">Bulk Upload Distributors</DialogTitle><p className="text-sm text-slate-500 mt-1">Upload an XLSX with Name, Region, State, District, Area, Contact, Phone, GST, Payment Terms.</p></DialogHeader>
        <div className="space-y-3">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="dist-template"><Download className="h-4 w-4 mr-1" /> Download Template</Button>
          <div><Label>XLSX File</Label><Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} data-testid="dist-bulk-file" /></div>
          {report && <div className="text-sm text-slate-700 border border-slate-200 rounded-lg p-3 bg-slate-50"><div><b>Created:</b> {report.created}</div><div><b>Skipped duplicates:</b> {report.skipped_duplicates}</div>{report.errors?.length > 0 && <div className="text-red-600 text-xs mt-1">{report.errors.length} error(s)</div>}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={upload} disabled={busy || !file} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="dist-bulk-upload"><Upload className="h-4 w-4 mr-1" /> {busy ? "Uploading…" : "Upload"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Distributors() {
  const { user, can } = useAuth();
  const [list, setList] = useState([]);
  const [regions, setRegions] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [open, setOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  async function load() { const { data } = await api.get("/distributors"); setList(data); setSelected(new Set()); }
  async function loadRegions() { const { data } = await api.get("/regions"); setRegions(data); }
  async function loadTerritories() { try { const { data } = await api.get("/territories", { params: { active: true } }); setTerritories(data || []); } catch { /* read-allowed for all roles, but tolerate failure */ } }
  useEffect(() => { load(); loadRegions(); loadTerritories(); }, []);

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} distributor(s)? Any orders they have will be orphaned.`)) return;
    try {
      const { data } = await api.post("/distributors/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} deleted`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  function startEdit(d) { setForm({ ...EMPTY, ...d }); setEditId(d.id); setOpen(true); }
  function startNew() { setForm(EMPTY); setEditId(null); setOpen(true); }

  async function save() {
    try {
      const payload = { ...form };
      if (!payload.region && payload.region_id) { const r = regions.find((x) => x.id === payload.region_id); if (r) payload.region = r.name; }
      if (editId) await api.patch(`/distributors/${editId}`, payload); else await api.post("/distributors", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  const filtered = list.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const hay = [d.name, d.area, d.district, d.state, d.contact_person, d.contact, d.mobile, d.phone, d.gst].filter(Boolean).join(" ").toLowerCase();
    return hay.includes(q);
  });
  function toggleAll() { setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map((d) => d.id))); }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Distributors</h1>
          <p className="text-sm text-slate-500 mt-1">Master data with area, district, state, contact and GSTIN. Search works across all fields.</p>
        </div>
        {can(["pfi"]) && (
          <div className="flex gap-2">
            {isAdmin && selected.size > 0 && <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="dist-bulk-delete"><Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size}</Button>}
            {can([]) && <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="dist-bulk-btn"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Bulk Upload</Button>}
            <Button onClick={startNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-distributor-btn"><Plus className="h-4 w-4 mr-1.5" /> New Distributor</Button>
          </div>
        )}
      </div>
      <Input placeholder="Search by firm, area, district, state, contact person, mobile, or GST" value={search} onChange={(e) => setSearch(e.target.value)} className="max-w-xl" data-testid="dist-master-search" />
      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                {isAdmin && <th className="p-3.5 w-8"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleAll} data-testid="dist-select-all" /></th>}
                <th className="p-3.5 text-left">Area · Town</th>
                <th className="p-3.5 text-left">Firm Name</th>
                <th className="p-3.5 text-left">District</th>
                <th className="p-3.5 text-left">State</th>
                <th className="p-3.5 text-left">Contact Person</th>
                <th className="p-3.5 text-left">Mobile</th>
                <th className="p-3.5 text-left">GSTIN</th>
                <th className="p-3.5 text-left">Status</th>
                <th className="p-3.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((d) => (
                <tr key={d.id} className="table-row-hover">
                  {isAdmin && <td className="p-3.5"><Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} data-testid={`dist-sel-${d.id}`} /></td>}
                  <td className="p-3.5 font-bold text-slate-900 text-[14px]">{d.area || "—"}</td>
                  <td className="p-3.5 text-slate-700 text-[13px]">{d.name}</td>
                  <td className="p-3.5 text-slate-700 text-xs">{d.district || "—"}</td>
                  <td className="p-3.5 text-slate-700 text-xs">{d.state || "—"}</td>
                  <td className="p-3.5 text-slate-700 text-xs">{d.contact_person || "—"}</td>
                  <td className="p-3.5 mono text-[11px]">{d.mobile || d.phone || "—"}</td>
                  <td className="p-3.5 mono text-[11px]">{d.gst || "—"}</td>
                  <td className="p-3.5"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${d.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{d.status || "Active"}</span></td>
                  <td className="p-3.5 text-right">{can([]) && <Button variant="ghost" size="icon" onClick={() => startEdit(d)} data-testid={`edit-distributor-${d.id}`}><Pencil className="h-4 w-4" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="heading">{editId ? "Edit" : "New"} Distributor</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Region</Label>
              <Select value={form.region_id} onValueChange={(v) => { const r = regions.find((x) => x.id === v); setForm({ ...form, region_id: v, region: r?.name || "" }); }}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>State</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label>District</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></div>
            <div><Label>Area</Label><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} /></div>
            <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Mobile Number</Label><Input value={form.mobile || form.phone || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value, phone: e.target.value })} /></div>
            <div><Label>GSTIN</Label><Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} /></div>
            <div><Label>Payment Terms</Label><Input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} /></div>
            <div className="col-span-2"><Label>Territory <span className="text-[10px] text-slate-400">(optional · W-2)</span></Label>
              <Select value={form.territory_id || "none"} onValueChange={(v) => { const t = territories.find((x) => x.id === v); setForm({ ...form, territory_id: v === "none" ? "" : v, territory_name: v === "none" ? "" : (t?.name || "") }); }}>
                <SelectTrigger data-testid="dist-territory-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {territories.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} <span className="text-[10px] text-slate-400 ml-1">({t.state} / {t.district})</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-distributor">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkUploadDialog open={bulkOpen} onOpenChange={setBulkOpen} onDone={load} />
    </div>
  );
}
